/**
 * AI Confidence Indicator Utilities
 * Provides functions for displaying and interpreting AI confidence levels
 */
/**
 * Confidence level thresholds and styling
 */
export const CONFIDENCE_LEVELS = [
    {
        level: 'very_low',
        label: 'Very Low',
        color: '#ef4444', // red-500
        description: 'AI has limited confidence in this recommendation',
        threshold: 0.2,
    },
    {
        level: 'low',
        label: 'Low',
        color: '#f97316', // orange-500
        description: 'AI has some uncertainty about this recommendation',
        threshold: 0.4,
    },
    {
        level: 'medium',
        label: 'Medium',
        color: '#eab308', // yellow-500
        description: 'AI has moderate confidence in this recommendation',
        threshold: 0.6,
    },
    {
        level: 'high',
        label: 'High',
        color: '#22c55e', // green-500
        description: 'AI is confident in this recommendation',
        threshold: 0.8,
    },
    {
        level: 'very_high',
        label: 'Very High',
        color: '#16a34a', // green-600
        description: 'AI is very confident in this recommendation',
        threshold: 1.0,
    },
];
/**
 * Get confidence level based on numeric confidence score
 */
export function getConfidenceLevel(confidence) {
    // Clamp confidence between 0 and 1
    const clampedConfidence = Math.max(0, Math.min(1, confidence));
    // Find the appropriate confidence level
    for (const level of CONFIDENCE_LEVELS) {
        if (clampedConfidence <= level.threshold) {
            return level;
        }
    }
    // Fallback to highest level
    return CONFIDENCE_LEVELS[CONFIDENCE_LEVELS.length - 1];
}
/**
 * Format confidence as percentage
 */
export function formatConfidencePercentage(confidence) {
    const percentage = Math.round(confidence * 100);
    return `${percentage}%`;
}
/**
 * Get confidence indicator icon based on level
 */
export function getConfidenceIcon(confidence) {
    const level = getConfidenceLevel(confidence);
    switch (level.level) {
        case 'very_low':
            return '⚠️';
        case 'low':
            return '🔸';
        case 'medium':
            return '🔶';
        case 'high':
            return '✅';
        case 'very_high':
            return '🎯';
        default:
            return '❓';
    }
}
/**
 * Generate accessibility label for confidence indicator
 */
export function getConfidenceA11yLabel(confidence) {
    const level = getConfidenceLevel(confidence);
    const percentage = formatConfidencePercentage(confidence);
    return `AI confidence: ${level.label} (${percentage}). ${level.description}`;
}
/**
 * Determine if confidence is sufficient for automatic actions
 */
export function isConfidenceSufficient(confidence, threshold = 0.7) {
    return confidence >= threshold;
}
/**
 * Get confidence-based styling properties
 */
export function getConfidenceStyles(confidence) {
    const level = getConfidenceLevel(confidence);
    return {
        color: level.color,
        backgroundColor: `${level.color}20`, // 20% opacity
        borderColor: level.color,
        opacity: Math.max(0.3, confidence), // Minimum 30% opacity
    };
}
/**
 * Generate confidence warning message if needed
 */
export function getConfidenceWarning(confidence) {
    const level = getConfidenceLevel(confidence);
    if (level.level === 'very_low' || level.level === 'low') {
        return `This recommendation has ${level.label.toLowerCase()} confidence. Consider reviewing alternatives or providing more information to improve accuracy.`;
    }
    return null;
}
/**
 * Calculate confidence trend based on historical data
 */
export function calculateConfidenceTrend(confidenceHistory) {
    if (confidenceHistory.length < 2) {
        return 'stable';
    }
    const recent = confidenceHistory.slice(-3); // Last 3 values
    const older = confidenceHistory.slice(-6, -3); // Previous 3 values
    if (recent.length === 0 || older.length === 0) {
        return 'stable';
    }
    const recentAvg = recent.reduce((sum, val) => sum + val, 0) / recent.length;
    const olderAvg = older.reduce((sum, val) => sum + val, 0) / older.length;
    const difference = recentAvg - olderAvg;
    const threshold = 0.05; // 5% threshold for trend detection
    if (difference > threshold) {
        return 'improving';
    }
    else if (difference < -threshold) {
        return 'declining';
    }
    else {
        return 'stable';
    }
}
/**
 * Aggregate confidence from multiple sources
 */
export function aggregateConfidence(confidenceScores) {
    if (confidenceScores.length === 0) {
        return 0;
    }
    const totalWeight = confidenceScores.reduce((sum, score) => sum + (score.weight || 1), 0);
    const weightedSum = confidenceScores.reduce((sum, score) => sum + score.value * (score.weight || 1), 0);
    return Math.max(0, Math.min(1, weightedSum / totalWeight));
}
/**
 * Generate confidence explanation text
 */
export function generateConfidenceExplanation(confidence, factors) {
    const level = getConfidenceLevel(confidence);
    const percentage = formatConfidencePercentage(confidence);
    let explanation = `AI confidence: ${percentage} (${level.label}). `;
    if (factors && factors.length > 0) {
        explanation += `Based on: ${factors.join(', ')}.`;
    }
    if (level.level === 'very_low' || level.level === 'low') {
        explanation +=
            ' Consider providing more information for better recommendations.';
    }
    return explanation;
}
